import { useCallback, useEffect, useMemo, useState } from 'react';
import useApiClient from '../api/useApiClient';
import { computeOwnerSetupStatus } from './ownerSetupHelpers';

/**
 * Reads the current company's server-persisted onboarding steps and reports
 * whether the restaurant owner still has setup phases to complete — the signal
 * powering the two-actor flow's owner entry-point (persistent "Setting up" card,
 * FOO-6351 C10).
 *
 * `enabled` short-circuits the fetch for users who are not restaurant owners
 * (partners, master providers) so the entry-point costs nothing for them.
 *
 * `failed` says the steps could not be read. The card then stays away: "Finish
 * setting up" over a wizard that cannot load its progress is a dead end.
 *
 * @param {boolean} [enabled=true]
 */
export default function useOwnerSetupStatus(enabled = true) {
  const { OnboardingStepsManager } = useApiClient();

  const [status, setStatus] = useState({ isComplete: false, firstIncompletePhaseKey: null, phases: [] });
  const [loading, setLoading] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const [failed, setFailed] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setInitialised(true);

      return;
    }

    setLoading(true);
    const { success, data } = await OnboardingStepsManager.get();
    setLoading(false);
    setFailed(!success);
    setInitialised(true);

    if (!success) {
      return;
    }

    setStatus(computeOwnerSetupStatus(data));
  }, [enabled, OnboardingStepsManager]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return useMemo(
    () => ({
      loading,
      initialised,
      failed,
      isComplete: status.isComplete,
      firstIncompletePhaseKey: status.firstIncompletePhaseKey,
      phases: status.phases,
      doneCount: status.doneCount ?? 0,
      refresh,
    }),
    [loading, initialised, failed, status, refresh],
  );
}
